import React from 'react'
import PropTypes from 'prop-types'
import { isInclusivelyBeforeDay } from 'react-dates'
import Select from 'react-select'

import moment from 'moment'
import VMasker from 'vanilla-masker'

import { CustomMenuList as MenuList } from 'component/reactSelect/CustomMenuList'
import { CustomMultiValue as MultiValueContainer } from 'component/reactSelect/CustomMultiValue'
import Col from 'component/utils/Col'
import DatePicker from 'component/utils/DatePicker'
import Field from 'component/utils/Field'
import Form from 'component/utils/Form'
import HierarchySelect from 'component/hierarchy/HierarchySelect'
import Input from 'component/utils/Input'
import RegionBranchMultipleSelect from 'component/branch/RegionBranchMultipleSelect'
import Row from 'component/utils/Row'
import StatusType from 'constants/StatusType'

const ALL = 'all'

const FIELDS = [
  'branchIdList',
  'cdLm',
  'dtEnd',
  'dtStart',
  'sectionIdList',
  'statusType',
]

const SideFilterForm = ({
  auth: { user },
  auth: {
    user: { locationCode },
  },
  fields,
  filtersVisible = false,
  handleSideFilterElementChange,
  setValueToField,
  sideFilterVisible,
  submit,
  translate,
  values,
}) => {
  const handleSubmit = (event) => {
    event.preventDefault()

    const branchIdList =
      locationCode === '999'
        ? values.branchIdList.reduce((acc, cur) => {
            if (cur.idBranches) return [...acc, ...cur.idBranches]

            return ALL !== cur.value ? [...acc, cur.value] : [...acc]
          }, [])
        : [locationCode.padStart(4, '0')]

    const sectionIdList = values.sectionIdList.reduce(
      (acc, cur) => (ALL !== cur.value ? [...acc, cur.value] : [...acc]),
      []
    )

    const params = {
      ...values,
      branchIdList,
      sectionIdList,
    }

    submit(params)
  }

  const handleChange = (field, values) => {
    const formattedValues = values || []

    setValueToField(field, formattedValues)
  }

  const statusOptions = StatusType.allStatus().map((i) => ({
    ...i,
    label: translate(i.label),
  }))

  return (
    <div
      className='filter-adjustment side-filter'
      ref={handleSideFilterElementChange}
      style={{ display: sideFilterVisible ? 'block' : 'none' }}
    >
      {filtersVisible && (
        <Field
          {...fields.branchIdList}
          hint={translate('delimit.report.store.hint')}
        >
          <RegionBranchMultipleSelect
            {...fields.branchIdList.input}
            allOption
            className='react-select-collapsible'
            components={{ MenuList, MultiValueContainer }}
            id='branchIdList'
            onChange={(event) =>
              setValueToField(
                fields.branchIdList.input.name,
                event.target.value
              )
            }
            placeholder={translate('field.store')}
            translate={translate}
          />
        </Field>
      )}

      <Field
        {...fields.sectionIdList}
        hint={translate('delimit.report.section.hint')}
      >
        <HierarchySelect
          allOption
          components={{ MenuList, MultiValueContainer }}
          disabled={!!values.cdLm}
          id='sectionIdList'
          isMulti
          name='sectionIdList'
          onChange={(values) => handleChange('sectionIdList', values)}
          placeholder={translate('section')}
          query={{ level: 2 }}
          selected
          translate={translate}
          user={user}
          value={values.sectionIdList}
        />
      </Field>

      <Field {...fields.cdLm} hint={translate('delimit.report.lm.hint')}>
        <Input
          {...fields.cdLm.input}
          className='default-input'
          disabled={!!values.sectionIdList.length}
          id='cdLm'
          onChange={(event) =>
            setValueToField(
              fields.cdLm.input.name,
              VMasker.toNumber(event.target.value)
            )
          }
          placeholder={translate('lm_label')}
        />
      </Field>

      <Field
        {...fields.statusType}
        hint={translate('delimit.report.status.hint')}
      >
        <Select
          {...fields.statusType.input}
          className='react-select react-select-multi'
          classNamePrefix='react-select'
          id='statusType'
          onChange={(option) =>
            setValueToField(fields.statusType.input.name, option.value)
          }
          options={statusOptions}
          placeholder={translate('delimit.report.status.placeholder')}
          value={statusOptions.find((i) => i.value === values.statusType)}
        />
      </Field>

      <Row>
        <Col xs={6}>
          <Field
            {...fields.dtStart}
            className='margin-bottom-small'
            glyph='glyph glyph-calendar'
            hint={translate('delimit.report.dtStart.hint')}
          >
            <DatePicker
              {...fields.dtStart.input}
              id={fields.dtStart.input.name}
              isOutsideRange={(date) =>
                moment(date).isAfter(values.dtEnd) ||
                !isInclusivelyBeforeDay(date, moment())
              }
              openUp='up'
              placeholder={translate('field.from')}
              scrollIntoView={false}
              showClearDate
            />
          </Field>
        </Col>

        <Col xs={6}>
          <Field
            {...fields.dtEnd}
            className='margin-bottom-small'
            glyph='glyph glyph-calendar'
            hint={translate('delimit.report.dtEnd.hint')}
          >
            <DatePicker
              {...fields.dtEnd.input}
              id={fields.dtEnd.input.name}
              isOutsideRange={(date) =>
                moment(date).isBefore(values.dtStart) ||
                !isInclusivelyBeforeDay(date, moment())
              }
              openUp='up'
              placeholder={translate('field.to')}
              scrollIntoView={false}
              showClearDate
            />
          </Field>
        </Col>
      </Row>

      <Row>
        <Col xs={12} className='margin-top-double margin-bottom'>
          <button
            className='button button-primary button-full'
            disabled={!values.dtEnd || !values.dtStart}
            onClick={handleSubmit}
            role='button'
            type='button'
          >
            {translate('filter_button_label')}
          </button>
        </Col>
      </Row>
    </div>
  )
}

SideFilterForm.propTypes = {
  auth: PropTypes.object.isRequired,
  fields: PropTypes.object.isRequired,
  filtersVisible: PropTypes.bool,
  handleSideFilterElementChange: PropTypes.func.isRequired,
  setValueToField: PropTypes.func.isRequired,
  sideFilterVisible: PropTypes.bool.isRequired,
  submit: PropTypes.func.isRequired,
  translate: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
}

export default Form({ fields: FIELDS })(SideFilterForm)

export { SideFilterForm }
